/* oxlint-disable react-hooks/rules-of-hooks */
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import PriceRowLoyaltyPointsPromoPill from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsPromoPill'
import PriceRowLoyaltyPointsLoadingSkeleton from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import { useAppSelector } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import determineLoyaltyBonusPointsCampaignType from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import recalculatePointsEarnAfterBurn from 'loyalty/lib/pointsCalculation/recalculatePointsEarnAfterBurn'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import type { ComponentProps } from 'react'
import { useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import GeoContext from 'contexts/geoContext'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import styled from 'styled-components'

type InheritedPriceRowLoyaltyPointsProps = Pick<ComponentProps<typeof PriceRowLoyaltyPoints>, 'onInfoIconClick' | 'calculationType' | 'horizontalAlign' | 'hideTierIcon'>

interface Props extends InheritedPriceRowLoyaltyPointsProps {
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined
  /**
   * @default 'S'
   */
  size?: React.ComponentProps<typeof PriceRowLoyaltyPoints>['size']
  /**
   * @default false
   */
  hideStatusCredits?: boolean
  /**
   * @default false
   */
  hidePromoPill?: boolean
  offer?: App.Offer | App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | App.ExperienceOffer | App.CruiseOffer
  /**
   * When set, scales earned points and status credits based on the remaining
   * cash after points are applied. If the price is fully covered, the
   * component is hidden.
   */
  pointsToApply?: number
  /** Full burn price returned by the burn API for the displayed price. */
  burnPriceInPoints?: number
}

const Container = styled.div`
  max-width: 100%;
  width: 100%;
`

function _LoyaltyPointsEarn(props: Props) {
  const {
    calculationRequests,
    size = 'S',
    hideStatusCredits = false,
    hidePromoPill = false,
    calculationType,
    horizontalAlign,
    onInfoIconClick,
    pointsToApply,
    burnPriceInPoints,
    hideTierIcon,
  } = props
  const { currentRegionCode } = useContext(GeoContext)
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)

  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const shouldHideStatusCreditsAndNights = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const shouldHideStatusCredits = hideStatusCredits || shouldHideStatusCreditsAndNights

  const { totalPointsCalculation, pointsCalculations } = useLoyaltyPointsEarnCalculator(calculationRequests)

  const initial = totalPointsCalculation?.initial
  const fetching = !!totalPointsCalculation?.fetching
  const err = totalPointsCalculation?.error
  const points = totalPointsCalculation?.data?.points ?? 0
  const pointsEarnRatio = totalPointsCalculation?.data?.roundedPointsEarnRatio ?? 0
  const campaignTypeFromCalculation = useMemo(() =>
    totalPointsCalculation?.data ? determineLoyaltyBonusPointsCampaignType(totalPointsCalculation.data) : [],
  [totalPointsCalculation?.data])

  const bonusPointsCampaignTypes = campaignTypeFromCalculation.length > 0 ? campaignTypeFromCalculation : undefined
  const burnAdjustedTotals = useMemo(() => {
    if (!pointsToApply || pointsToApply <= 0 || !burnPriceInPoints || burnPriceInPoints <= 0) return undefined

    const completedCalculations = nonNullable(pointsCalculations.map(pointsCalculation => pointsCalculation?.data))
    if (!completedCalculations.length) return undefined

    return recalculatePointsEarnAfterBurn(completedCalculations, pointsToApply, burnPriceInPoints)
  }, [burnPriceInPoints, pointsCalculations, pointsToApply])

  const displayedPoints = burnAdjustedTotals?.points ?? points
  const displayedStatusCredits = burnAdjustedTotals
    ? burnAdjustedTotals.statusCredits + burnAdjustedTotals.bonusStatusCredits
    : (totalPointsCalculation?.data?.statusCredits ?? 0) + (totalPointsCalculation?.data?.bonusStatusCredits ?? 0)

  // Keep showing the last successfully loaded values while a new calculation is in
  // flight (e.g. ticket count changed) instead of flashing back to the skeleton.
  const lastGoodValueRef = useRef<{
    points: number
    statusCredits: number
    pointsEarnRatio: number
    bonusPointsCampaignTypes?: Array<App.LoyaltyBonusPointsCampaignType>
  } | undefined>(undefined)

  useEffect(() => {
    if (fetching || initial || err) return
    lastGoodValueRef.current = {
      points: displayedPoints,
      statusCredits: displayedStatusCredits,
      pointsEarnRatio,
      bonusPointsCampaignTypes,
    }
  }, [fetching, initial, err, displayedPoints, displayedStatusCredits, pointsEarnRatio, bonusPointsCampaignTypes])

  const isRefetching = (fetching || initial) && !!lastGoodValueRef.current
  const showSkeleton = (fetching || initial) && !lastGoodValueRef.current

  const resolvedPoints = isRefetching ? lastGoodValueRef.current!.points : displayedPoints
  const resolvedStatusCredits = isRefetching ? lastGoodValueRef.current!.statusCredits : displayedStatusCredits
  const resolvedPointsEarnRatio = isRefetching ? lastGoodValueRef.current!.pointsEarnRatio : pointsEarnRatio
  const resolvedBonusPointsCampaignTypes = isRefetching ? lastGoodValueRef.current!.bonusPointsCampaignTypes : bonusPointsCampaignTypes

  if (
    !loyaltyEnabled ||
    !calculationRequests ||
    (Array.isArray(calculationRequests) && nonNullable(calculationRequests).length === 0) ||
    err ||
    (!initial && !fetching && !displayedPoints && !isRefetching) ||
    (!!accountRegion && accountRegion !== currentRegionCode) ||
    (burnAdjustedTotals?.remainingCash !== undefined && burnAdjustedTotals.remainingCash <= 0)
  ) return null
  return <Container>
    {showSkeleton && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size={size} hideTierIcon={hideTierIcon} />}
    {!showSkeleton && <>
      {(!resolvedBonusPointsCampaignTypes || hidePromoPill) && <PriceRowLoyaltyPoints
        hideTierIcon={hideTierIcon}
        tier={displayTier}
        earnablePoints={resolvedPoints}
        size={size}
        calculationType={calculationType}
        statusCredits={shouldHideStatusCredits ? undefined : resolvedStatusCredits}
        pointsEarnRatio={resolvedPointsEarnRatio}
        horizontalAlign={horizontalAlign}
        onInfoIconClick={onInfoIconClick}
      />}
      {!!resolvedBonusPointsCampaignTypes && !hidePromoPill && <PriceRowLoyaltyPointsPromoPill
        hideTierIcon={hideTierIcon}
        campaigns={resolvedBonusPointsCampaignTypes}
        tier={displayTier}
        earnablePoints={resolvedPoints}
        size={size}
        calculationType={calculationType}
        statusCredits={shouldHideStatusCredits ? undefined : resolvedStatusCredits}
        pointsEarnRatio={resolvedPointsEarnRatio}
        horizontalAlign={horizontalAlign}
        onPillClick={onInfoIconClick}
      />}
    </>}
  </Container>
}

export default _LoyaltyPointsEarn
